<template>
  <div>
    <!-- Overlay -->
    <transition name="ai-fade">
      <div v-if="open" class="ai-overlay" @click="closePanel"></div>
    </transition>

    <!-- Side Panel -->
    <transition name="ai-slide">
      <div v-if="open" class="ai-panel" ref="aiPanel" :style="panelStyle">
        <div class="ai-panel-header">
          <div class="ai-header-left">
            <div class="ai-avatar-header">
              <img src="/storage/agent_images/WczuctaLYFehJGAsjN7DWfDUJwymKUnPCoOacJIN.png" alt="Alexander" />
              <span class="ai-avatar-online"></span>
            </div>
            <div class="ai-header-info">
              <div class="ai-panel-name">Alexander</div>
              <div class="ai-panel-role">Personal Concierge</div>
            </div>
          </div>
          <button class="ai-panel-close" @click="closePanel">
            <Icon icon="ph:x-bold" class="iconify" />
          </button>
        </div>

        <div class="ai-panel-messages" ref="messagesContainer">
          <div
            v-for="(msg, i) in messages"
            :key="i"
            class="ai-msg"
            :class="msg.role === 'user' ? 'ai-msg--user' : 'ai-msg--bot'"
          >
            <div v-if="msg.role === 'assistant'" class="ai-msg-avatar">
              <img src="/storage/agent_images/WczuctaLYFehJGAsjN7DWfDUJwymKUnPCoOacJIN.png" alt="Alexander" />
            </div>
            <div class="ai-msg-content">
              <div class="ai-msg-text" v-html="formatMessage(msg.text)"></div>
              <!-- Vehicle cards -->
              <div v-if="msg.vehicles && msg.vehicles.length" class="ai-vehicles">
                <router-link
                  v-for="v in msg.vehicles"
                  :key="v.id"
                  :to="`/car/${slugify(v.make || 'car')}/${slugify(v.model || 'model')}/${v.year || 'new'}/${v.id}`"
                  class="ai-vehicle-card"
                  @click.native="open = false"
                >
                  <img v-if="v.image" :src="v.image" :alt="v.make + ' ' + v.model" />
                  <div class="ai-vehicle-info">
                    <div class="ai-vehicle-name">{{ v.year }} {{ v.make }} {{ v.model }}</div>
                    <div class="ai-vehicle-price" v-if="v.price">{{ formatPrice(v.price) }}</div>
                  </div>
                </router-link>
              </div>
              <!-- Quick action buttons -->
              <div v-if="msg.showActions" class="ai-quick-actions">
                <a href="https://wa.me/421949006016" target="_blank" class="ai-action-btn ai-action-btn--wa">
                  <Icon icon="uil:whatsapp" class="iconify" /> WhatsApp
                </a>
                <a href="https://t.me/selectedslovakia" target="_blank" class="ai-action-btn ai-action-btn--tg">
                  <Icon icon="ph:telegram-logo" class="iconify" /> Telegram
                </a>
              </div>
            </div>
          </div>
          <div v-if="loading" class="ai-msg ai-msg--bot">
            <div class="ai-msg-avatar">
              <img src="/storage/agent_images/WczuctaLYFehJGAsjN7DWfDUJwymKUnPCoOacJIN.png" alt="Alexander" />
            </div>
            <div class="ai-msg-content">
              <div class="ai-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>

        <div class="ai-panel-footer" ref="panelFooter">
          <div class="ai-suggestions" v-if="messages.length <= 1">
            <button @click="sendQuick('I am looking for a luxury SUV')" class="ai-suggestion">Luxury SUV</button>
            <button @click="sendQuick('Show me sports cars')" class="ai-suggestion">Sports Cars</button>
            <button @click="sendQuick('What do you have under 100k?')" class="ai-suggestion">Under 100k</button>
          </div>
          <form @submit.prevent="send" class="ai-panel-input">
            <input
              v-model="input"
              type="text"
              inputmode="text"
              enterkeyhint="send"
              autocomplete="off"
              :placeholder="placeholder"
              :disabled="loading"
              maxlength="500"
              ref="chatInput"
              @keydown.enter.prevent="send"
            />
            <button type="submit" :disabled="!input.trim() || loading" @click.prevent="send">
              <Icon icon="ph:paper-plane-tilt-fill" class="iconify" />
            </button>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { Icon } from '@iconify/vue';
import { slugify } from '../seo.js';

export default {
  components: { Icon },
  data() {
    return {
      open: false,
      hasPlayedSound: false,
      input: '',
      messages: [
        {
          role: 'assistant',
          text: 'Good evening. I am Alexander, your personal concierge at Selected Cars. How may I assist you today?',
          vehicles: [],
          showActions: false,
        }
      ],
      history: [],
      loading: false,
      placeholder: 'Type your message...',
      isMobile: false,
      savedScrollY: 0,
    };
  },
  computed: {
    panelStyle() {
      return {};
    },
  },
  mounted() {
    this.isMobile = window.innerWidth < 768;
    window.addEventListener('resize', this.checkMobile);

    // visualViewport: resize panel to exactly visible area when keyboard opens
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportResize);
      window.visualViewport.addEventListener('scroll', this.onViewportResize);
    }

    setTimeout(() => {
      this.playNotificationSound();
    }, 3000);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewportResize);
      window.visualViewport.removeEventListener('scroll', this.onViewportResize);
    }
  },
  methods: {
    slugify,
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    onViewportResize() {
      if (!this.isMobile || !this.open) return;
      const panel = this.$refs.aiPanel;
      if (!panel) return;

      const vv = window.visualViewport;
      // Set panel to exactly the visible viewport (above keyboard)
      panel.style.height = vv.height + 'px';
      panel.style.top = vv.offsetTop + 'px';

      this.$nextTick(() => this.scrollToBottom());
    },
    lockBody() {
      // iOS needs position:fixed to truly prevent background scroll
      this.savedScrollY = window.scrollY;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${this.savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.overflow = 'hidden';
      body.style.width = '100%';
    },
    unlockBody() {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.overflow = '';
      body.style.width = '';
      window.scrollTo(0, this.savedScrollY);
    },
    toggle() {
      if (this.open) {
        this.closePanel();
      } else {
        this.open = true;
        if (this.isMobile) {
          this.lockBody();
        }
      }
    },
    closePanel() {
      // Blur input first to dismiss keyboard
      if (this.$refs.chatInput) {
        this.$refs.chatInput.blur();
      }
      // Reset panel inline styles from viewport handler
      const panel = this.$refs.aiPanel;
      if (panel) {
        panel.style.height = '';
        panel.style.top = '';
      }
      this.open = false;
      if (this.isMobile) {
        this.unlockBody();
      }
    },
    playNotificationSound() {
      if (this.hasPlayedSound) return;
      this.hasPlayedSound = true;
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const playTone = (freq, startTime, duration) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.connect(gain);
          gain.connect(audioCtx.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0, audioCtx.currentTime + startTime);
          gain.gain.linearRampToValueAtTime(0.08, audioCtx.currentTime + startTime + 0.05);
          gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + startTime + duration);
          osc.start(audioCtx.currentTime + startTime);
          osc.stop(audioCtx.currentTime + startTime + duration);
        };
        playTone(880, 0, 0.3);
        playTone(1318, 0.15, 0.4);
      } catch (e) {}
    },
    formatMessage(text) {
      if (!text) return '';
      text = text.replace(/\+421\s?949\s?006\s?016/g, '<a href="https://wa.me/421949006016" target="_blank" class="ai-link">+421 949 006 016</a>');
      text = text.replace(/@selectedslovakia/g, '<a href="https://t.me/selectedslovakia" target="_blank" class="ai-link">@selectedslovakia</a>');
      return text;
    },
    formatPrice(price) {
      if (!price) return '';
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price) + '\u20AC';
    },
    sendQuick(text) {
      this.input = text;
      this.send();
    },
    async send() {
      const text = this.input.trim();
      if (!text || this.loading) return;

      this.messages.push({ role: 'user', text, vehicles: [], showActions: false });
      this.input = '';
      this.loading = true;
      this.scrollToBottom();

      try {
        const res = await axios.post('/api/ai-concierge', {
          message: text,
          history: this.history,
        });

        this.history.push({ role: 'user', content: text });
        this.history.push({ role: 'assistant', content: res.data.text });

        if (this.history.length > 10) {
          this.history = this.history.slice(-10);
        }

        const hasContact = /whatsapp|telegram|contact.*directly/i.test(res.data.text);

        this.messages.push({
          role: 'assistant',
          text: res.data.text,
          vehicles: res.data.vehicles || [],
          showActions: hasContact,
        });

        this.playMessageSound();
      } catch (err) {
        this.messages.push({
          role: 'assistant',
          text: 'I do apologise \u2014 I am momentarily unavailable. Please reach our team directly on WhatsApp at +421 949 006 016.',
          vehicles: [],
          showActions: true,
        });
      } finally {
        this.loading = false;
        this.scrollToBottom();
      }
    },
    playMessageSound() {
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = 660;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.04, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.2);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.2);
      } catch (e) {}
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style>
/* ========================================
   AI CONCIERGE PANEL
   ======================================== */

.ai-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9998;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

/* Panel: fullscreen on mobile, 460px side panel on desktop */
.ai-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #0d0d0d;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (min-width: 768px) {
  .ai-panel {
    left: auto;
    width: 460px;
    box-shadow: -8px 0 40px rgba(0, 0, 0, 0.5);
  }
}

/* Header */
.ai-panel-header {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}

.ai-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
}

.ai-header-info {
  min-width: 0;
}

.ai-avatar-header {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  border: 2px solid rgba(255,255,255,0.1);
}
.ai-avatar-header img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ai-avatar-online {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 11px;
  height: 11px;
  background: #25d366;
  border-radius: 50%;
  border: 2px solid #0d0d0d;
}

.ai-panel-name {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
}
.ai-panel-role {
  font-size: 11px;
  color: #666;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 2px;
}

.ai-panel-close {
  background: rgba(255,255,255,0.06);
  border: none;
  color: #888;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  margin-left: 8px;
}
.ai-panel-close:hover {
  color: #fff;
  background: rgba(255,255,255,0.1);
}

/* Messages */
.ai-panel-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  scrollbar-width: none;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
.ai-panel-messages::-webkit-scrollbar { display: none; }

/* Message row */
.ai-msg {
  display: flex;
  gap: 8px;
  max-width: 100%;
  width: fit-content;
}
.ai-msg--bot {
  align-self: flex-start;
  max-width: 92%;
}
.ai-msg--user {
  align-self: flex-end;
  flex-direction: row-reverse;
  max-width: 82%;
}

.ai-msg-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  margin-top: 2px;
}
.ai-msg-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ai-msg-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
}

.ai-msg-text {
  padding: 12px 14px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.ai-msg--bot .ai-msg-text {
  background: #1a1a1a;
  color: #d8d8d8;
  border-top-left-radius: 4px;
}

.ai-msg--user .ai-msg-text {
  background: #fff;
  color: #111;
  border-top-right-radius: 4px;
}

.ai-link {
  color: #25d366;
  text-decoration: none;
  font-weight: 500;
}
.ai-link:hover {
  text-decoration: underline;
}

/* Quick Actions */
.ai-quick-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.ai-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}
.ai-action-btn--wa { background: #25d366; color: #fff; }
.ai-action-btn--wa:hover { background: #20bd5a; }
.ai-action-btn--tg { background: #2aabee; color: #fff; }
.ai-action-btn--tg:hover { background: #229ed9; }

/* Vehicle cards — always vertical list (like WhatsApp/iMessage link previews) */
.ai-vehicles {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.ai-vehicle-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  transition: border-color 0.2s;
  border: 1px solid rgba(255,255,255,0.06);
}
.ai-vehicle-card:hover {
  border-color: rgba(255,255,255,0.15);
}
.ai-vehicle-card img {
  width: 80px;
  height: 56px;
  object-fit: cover;
  flex-shrink: 0;
}
.ai-vehicle-info {
  padding: 6px 10px;
  min-width: 0;
  flex: 1;
}
.ai-vehicle-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ai-vehicle-price {
  font-size: 11.5px;
  color: #777;
  margin-top: 1px;
}

/* Typing */
.ai-typing {
  display: flex;
  gap: 5px;
  padding: 12px 14px;
  background: #1a1a1a;
  border-radius: 18px;
  border-top-left-radius: 4px;
  width: fit-content;
}
.ai-typing span {
  width: 6px;
  height: 6px;
  background: #555;
  border-radius: 50%;
  animation: ai-typing-dot 1.2s ease-in-out infinite;
}
.ai-typing span:nth-child(2) { animation-delay: 0.15s; }
.ai-typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes ai-typing-dot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-4px); opacity: 1; }
}

/* Footer */
.ai-panel-footer {
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
  padding-bottom: env(safe-area-inset-bottom, 0);
  background: #0d0d0d;
}

/* On mobile, make sure footer stays at bottom and is always tappable */
@media (max-width: 767px) {
  .ai-panel-footer {
    padding-bottom: max(env(safe-area-inset-bottom, 8px), 8px);
  }
}

/* Suggestions */
.ai-suggestions {
  display: flex;
  gap: 6px;
  padding: 10px 16px 0;
  flex-wrap: wrap;
}
.ai-suggestion {
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  color: #aaa;
  padding: 7px 14px;
  border-radius: 18px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.ai-suggestion:hover {
  background: #222;
  color: #fff;
  border-color: rgba(255,255,255,0.15);
}

/* Input */
.ai-panel-input {
  display: flex;
  align-items: center;
}
.ai-panel-input input {
  flex: 1;
  background: transparent;
  border: none;
  color: #fff;
  padding: 14px 16px;
  font-size: 16px;
  outline: none;
  font-family: inherit;
  min-width: 0;
  /* 16px prevents iOS Safari auto-zoom on focus */
}
.ai-panel-input input::placeholder {
  color: #3a3a3a;
}
.ai-panel-input button {
  background: transparent;
  border: none;
  color: #555;
  padding: 14px 16px;
  cursor: pointer;
  transition: color 0.2s;
  font-size: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.ai-panel-input button:hover { color: #fff; }
.ai-panel-input button:disabled { color: #222; cursor: default; }

/* Bigger touch target for send button on mobile */
@media (max-width: 767px) {
  .ai-panel-input button {
    padding: 14px 18px;
    font-size: 22px;
    min-height: 48px;
    min-width: 48px;
    justify-content: center;
  }
  .ai-panel-input button:not(:disabled) {
    color: #fff;
  }
}

/* Desktop: bigger text + padding */
@media (min-width: 768px) {
  .ai-panel-header { padding: 18px 20px; }
  .ai-avatar-header { width: 46px; height: 46px; }
  .ai-panel-messages { padding: 20px; gap: 16px; }
  .ai-msg-avatar { width: 32px; height: 32px; }
  .ai-msg-text { font-size: 15px; padding: 12px 16px; }
  .ai-suggestions { padding: 12px 20px 0; }
  .ai-panel-input input { padding: 16px 20px; }
  .ai-panel-input button { padding: 16px 20px; }
  .ai-vehicle-card img { width: 90px; height: 64px; }
  .ai-vehicle-name { font-size: 13px; }
  .ai-vehicle-price { font-size: 12px; }
}

@media (min-width: 1400px) {
  .ai-panel { width: 500px; }
}

/* Mobile: hide overlay since panel is fullscreen */
@media (max-width: 767px) {
  .ai-overlay { display: none; }
}

/* Transitions */
.ai-slide-enter-active,
.ai-slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.ai-slide-enter-from,
.ai-slide-leave-to {
  transform: translateX(100%);
}
.ai-fade-enter-active,
.ai-fade-leave-active {
  transition: opacity 0.3s ease;
}
.ai-fade-enter-from,
.ai-fade-leave-to {
  opacity: 0;
}

/* Floating Button */
.ai-btn-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 14px;
  height: 14px;
  background: #25d366;
  border-radius: 50%;
  border: 2.5px solid #fff;
  animation: ai-btn-pulse 2s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}
@keyframes ai-btn-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(37,211,102,0.5); }
  50% { box-shadow: 0 0 0 5px rgba(37,211,102,0); }
}
.floating-btn--ai.ai-notify {
  animation: ai-ring-pulse 2s ease-out 3;
}
@keyframes ai-ring-pulse {
  0% { box-shadow: 0 0 0 0 rgba(37,211,102,0.45); }
  70% { box-shadow: 0 0 0 16px rgba(37,211,102,0); }
  100% { box-shadow: 0 0 0 0 rgba(37,211,102,0); }
}
</style>
